/* eslint-disable max-lines-per-function */
/* eslint-disable no-invalid-this */
import React from "react";
import { omit } from "lodash";
import { TextField, Autocomplete } from "@mui/material";
import { isInputValid } from "../form-utils";

const isNil = (value: any) => typeof value === "undefined" || value === null;

const propsToOmit = [
  "options",
  "formData",
  "onChangeAction",
  "onBlurAction",
  "persistTableName",
  "validationSchema",
  "onFreeSoloAdd",
];

export const SelectAutocomplete: React.FC<{
  name: string;
  margin: "none" | "dense";
  label: string;
  options: { label: string; value: string }[];
  onChangeAction?: (props: any, value: any) => void;
  onBlurAction?: (props: any) => void;
  validationSchema: any[];
  required?: boolean;
  defaultValue: string | null;
  value?: string | null;
  formData: any;
  size?: "small" | "medium";
  multiple?: boolean;
  freeSolo?: boolean;
  onFreeSoloAdd?: (value: string) => void;
  filterOptions?: (options: string[], state: { inputValue: string }) => string[];
}> = (args) => {
  const props = {
    ...args,
    onChangeAction: args.onChangeAction ?? (() => null),
    onBlurAction: args.onBlurAction ?? (() => null),
    validationSchema: args.validationSchema ?? [],
  };
  const [errMsg, setErrMsg] = React.useState("");
  const [selectedValue, setSelectedValue] = React.useState<string>(() => {
    const initial = (props.value ?? props.defaultValue) as string | null;
    return initial ?? "";
  });
  React.useEffect(() => {
    // keep internal value in sync if parent controls value
    if (typeof props.value !== "undefined" && props.value !== null) {
      setSelectedValue(props.value as string);
    }
  }, [props.value]);

  const handleBlur = () => {
    if (props.validationSchema.length > 0) {
      const [isValid, newErrMsg] = isInputValid(props);
      if (!isValid) setErrMsg(newErrMsg);
    }
    props.onBlurAction(props);
  };

  const newProps = omit(props, propsToOmit);
  const margin = props.margin || "dense";
  // prevent react from complaining about null values
  if (props.value === null) {
    newProps.value = "Select";
  } else if (props.defaultValue === null && !props.value) {
    newProps.defaultValue = null;
  } else if (isNil(props.defaultValue) && typeof props.value === "undefined") {
    newProps.defaultValue = null;
  }
  if (newProps.value) delete newProps.defaultValue;

  const options = props.options.map((option) => {
    if (typeof option !== "object") return { label: option, value: option };
    return option;
  });
  const initialOptionsToSelect = options.map(({ value }) => value);
  const initialOptionsMap = options.reduce((acc, item) =>
    ({ ...acc, [item.value]: item.label }), {}) as Record<string, string>;
  const [localOptions, setLocalOptions] = React.useState<string[]>(initialOptionsToSelect);
  const [localOptionsMap, setLocalOptionsMap] =
    React.useState<Record<string, string>>(initialOptionsMap);
  React.useEffect(() => {
    const nextValues = options.map(({ value }) => value);
    const nextMap = options.reduce((acc, item) =>
      ({ ...acc, [item.value]: item.label }), {} as Record<string, string>);
    // Merge new incoming options with any previously added freeSolo values
    setLocalOptions((prev) => {
      const merged = new Set<string>([...prev.filter(value => !(value in nextMap)), ...nextValues]);
      return Array.from(merged);
    });
    setLocalOptionsMap((prev) => ({ ...prev, ...nextMap }));
  }, [args.options]);

  return (
    <>
      <Autocomplete
        id={props.name}
        onChange={(_e, value) => {
          setSelectedValue((value as string) ?? "");
          props.onChangeAction(null, { target: { value, name: props.name } });
          // If freeSolo and new value not present, add to local options
          if (props.freeSolo) {
            const val = (value as string) || "";
            if (val && !localOptions.includes(val)) {
              setLocalOptions((prev) => [...prev, val]);
              setLocalOptionsMap((prev) => ({ ...prev, [val]: val }));
              props.onFreeSoloAdd?.(val);
            }
          }
        }}
        autoSelect={props.freeSolo}
        onBlur={handleBlur}
        onFocus={() => setErrMsg("")}
        required={props.required}
        fullWidth={typeof (newProps as any).fullWidth === "undefined" ? true : (newProps as any).fullWidth}
        {...newProps}
        renderOption={(liProps, option) => (
          <li {...liProps}>{localOptionsMap[(option as string)] || (option as string)}</li>
        )}
        getOptionLabel={(option) => localOptionsMap[option] || (option as string)}
        renderInput={(params) => <TextField
          {...params}
          label={props.label}
          margin={margin}
          required={props.required}
          error={!!errMsg}
          size={props.size}
        />}
        options={localOptions}
        multiple={props.multiple}

      />
      <input type="hidden" name={props.name} value={selectedValue} />
    </>
  );
};

export default SelectAutocomplete;

